import React, { useState, useMemo, useEffect } from 'react';
import { FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { MetricsByCountryItem } from '../../api/types';

interface MetricsByCountryTableProps {
  metricsData: MetricsByCountryItem[];
  loading: boolean;
  hideMetricTypeFilter?: boolean;
}

const MetricsByCountryTable: React.FC<MetricsByCountryTableProps> = ({ metricsData, loading, hideMetricTypeFilter = false }) => {
  const [selectedCountry, setSelectedCountry] = useState<string>('Tüm Ülkeler');
  const [selectedVersion, setSelectedVersion] = useState<string>('Tüm Versiyonlar');
  const [selectedMetricType, setSelectedMetricType] = useState<string>('Tüm Oturum Tipleri');
  const [sortField, setSortField] = useState<'total' | 'avg_duration' | 'app_id' | 'country_code'>('total');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 25;

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Format duration from seconds to readable format
  const formatDuration = (seconds: number): string => {
    if (seconds === 0) return '0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  // Get unique countries and versions from data
  const uniqueCountries = useMemo(() => {
    const countries = Array.from(new Set(metricsData.map(item => item.country_code))).sort();
    return ['Tüm Ülkeler', ...countries];
  }, [metricsData]);

  const uniqueVersions = useMemo(() => {
    const versions = Array.from(new Set(metricsData.map(item => item.app_id))).sort((a, b) => {
      // Sort versions semantically
      const parseVersion = (v: string): number[] => {
        return v.split('.').map(num => parseInt(num, 10) || 0);
      };
      const aParts = parseVersion(a);
      const bParts = parseVersion(b);
      const maxLength = Math.max(aParts.length, bParts.length);
      for (let i = 0; i < maxLength; i++) {
        const aPart = aParts[i] || 0;
        const bPart = bParts[i] || 0;
        if (aPart !== bPart) {
          return bPart - aPart; // Descending order
        }
      }
      return 0;
    });
    return ['Tüm Versiyonlar', ...versions];
  }, [metricsData]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCountry, selectedVersion, selectedMetricType]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...metricsData];

    // Filter by metric type (only if filter is visible)
    if (!hideMetricTypeFilter && selectedMetricType !== 'Tüm Oturum Tipleri') {
      filtered = filtered.filter(item => {
        const typeMap: Record<string, string> = {
          'App': 'app_session',
          'Game': 'game_session',
          'Test': 'test_session',
        };
        return item.metric_type === typeMap[selectedMetricType];
      });
    }

    // Filter by country
    if (selectedCountry !== 'Tüm Ülkeler') {
      filtered = filtered.filter(item => item.country_code === selectedCountry);
    }

    // Filter by version
    if (selectedVersion !== 'Tüm Versiyonlar') {
      filtered = filtered.filter(item => item.app_id === selectedVersion);
    }

    // Sort data - default to descending (büyükten küçüğe)
    return filtered.sort((a, b) => {
      if (sortField === 'total' || sortField === 'avg_duration') {
        let aValue: number;
        let bValue: number;

        if (sortField === 'total') {
          aValue = a.total;
          bValue = b.total;
        } else {
          aValue = a.avg_duration;
          bValue = b.avg_duration;
        }

        if (sortDirection === 'desc') {
          return bValue - aValue; // Büyükten küçüğe
        } else {
          return aValue - bValue; // Küçükten büyüğe
        }
      } else {
        // String sorting for app_id and country_code
        let aValue: string = sortField === 'app_id' ? a.app_id : a.country_code;
        let bValue: string = sortField === 'app_id' ? b.app_id : b.country_code;

        // Version sorting for app_id (semantic versioning)
        if (sortField === 'app_id') {
          const parseVersion = (v: string): number[] => {
            return v.split('.').map(num => parseInt(num, 10) || 0);
          };
          const aParts = parseVersion(aValue);
          const bParts = parseVersion(bValue);
          const maxLength = Math.max(aParts.length, bParts.length);
          for (let i = 0; i < maxLength; i++) {
            const aPart = aParts[i] || 0;
            const bPart = bParts[i] || 0;
            if (aPart !== bPart) {
              return sortDirection === 'desc' ? bPart - aPart : aPart - bPart;
            }
          }
          return 0;
        }

        // Alphabetical sorting for country_code
        if (sortDirection === 'desc') {
          return bValue.localeCompare(aValue);
        } else {
          return aValue.localeCompare(bValue);
        }
      }
    });
  }, [metricsData, selectedCountry, selectedVersion, selectedMetricType, sortField, sortDirection, hideMetricTypeFilter]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedData.slice(startIndex, endIndex);
  }, [filteredAndSortedData, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

  const handleSort = (field: 'total' | 'avg_duration' | 'app_id' | 'country_code') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-1">Ülkeye Göre Metrikler</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">Ülke ve uygulama versiyonuna göre oturum metrikleri (App, Game, Test)</p>
        </div>
        <div className="flex items-center gap-3">
          {!hideMetricTypeFilter && (
            <div className="relative">
              <select
                value={selectedMetricType}
                onChange={(e) => setSelectedMetricType(e.target.value)}
                className="appearance-none px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md min-w-[180px]"
              >
                <option value="Tüm Oturum Tipleri" className="bg-white dark:bg-gray-700">Tüm Oturum Tipleri</option>
                <option value="App" className="bg-white dark:bg-gray-700">App</option>
                <option value="Game" className="bg-white dark:bg-gray-700">Game</option>
                <option value="Test" className="bg-white dark:bg-gray-700">Test</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}
          <div className="relative">
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="appearance-none px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md min-w-[180px]"
            >
              {uniqueCountries.map((country) => (
                <option key={country} value={country} className="bg-white dark:bg-gray-700">
                  {country}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <div className="relative">
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              className="appearance-none px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md min-w-[180px]"
            >
              {uniqueVersions.map((version) => (
                <option key={version} value={version} className="bg-white dark:bg-gray-700">
                  {version}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600">OTURUM TİPİ</th>
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                onClick={() => handleSort('country_code')}
              >
                <div className="flex items-center gap-2">
                  <span>ÜLKE KODU</span>
                  {sortField === 'country_code' ? (
                    renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                  ) : (
                    <span className="w-4 h-4 opacity-30">
                      {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                    </span>
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                onClick={() => handleSort('app_id')}
              >
                <div className="flex items-center gap-2">
                  <span>UYGULAMA VERSİYONU</span>
                  {sortField === 'app_id' ? (
                    renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                  ) : (
                    <span className="w-4 h-4 opacity-30">
                      {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                    </span>
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                onClick={() => handleSort('total')}
              >
                <div className="flex items-center justify-end gap-2">
                  <span>TOPLAM OTURUM</span>
                  {sortField === 'total' ? (
                    renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                  ) : (
                    <span className="w-4 h-4 opacity-30">
                      {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                    </span>
                  )}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-right font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-200 select-none"
                onClick={() => handleSort('avg_duration')}
              >
                <div className="flex items-center justify-end gap-2">
                  <span>ORTALAMA SÜRE</span>
                  {sortField === 'avg_duration' ? (
                    renderIcon(sortDirection === 'desc' ? FiArrowDown : FiArrowUp, "w-4 h-4 text-indigo-600 dark:text-indigo-400")
                  ) : (
                    <span className="w-4 h-4 opacity-30">
                      {renderIcon(FiArrowDown, "w-4 h-4 text-slate-400")}
                    </span>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                  {loading ? 'Yükleniyor...' : 'Seçilen filtreler için metrik verisi bulunamadı'}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => {
                const getMetricTypeLabel = (type?: string) => {
                  switch (type) {
                    case 'app_session': return 'App';
                    case 'game_session': return 'Game';
                    case 'test_session': return 'Test';
                    default: return 'App';
                  }
                };

                const getMetricTypeColor = (type?: string) => {
                  switch (type) {
                    case 'app_session': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
                    case 'game_session': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
                    case 'test_session': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
                    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
                  }
                };

                return (
                  <tr 
                    key={index} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-gray-600">
                      <span className={`inline-block ${getMetricTypeColor(row.metric_type)} px-3 py-1 rounded font-semibold`}>
                        {getMetricTypeLabel(row.metric_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-gray-600">
                      <span className="inline-block bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 px-3 py-1 rounded font-semibold">
                        {row.country_code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400 border border-gray-200 dark:border-gray-600">
                      {row.app_id}
                    </td>
                    <td className="px-4 py-3 text-right border border-gray-200 dark:border-gray-600">
                      <span className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-1 rounded font-medium">
                        {row.total.toLocaleString('tr-TR')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 border border-gray-200 dark:border-gray-600">
                      {formatDuration(row.avg_duration)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Toplam {filteredAndSortedData.length} kayıttan {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}-{Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} arası gösteriliyor
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Önceki
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-indigo-600 dark:bg-indigo-500 text-white'
                        : 'text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Sonraki
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsByCountryTable;
